import axios from 'axios';
import Configuration from "../configuration";
import {logoutSuccess} from "../redux/actions/applicationData";
import {store} from "../services/StoreService";
import ToastService from "../services/ToastService";
import {loadRefreshToken, loadToken} from "../services/TokenService";
import {readStoredIdentifier} from "../services/FirebaseService";
import {refreshAccessToken, RefreshOutcome} from "../redux/sagas/applicationData";

const instance = axios.create({
    baseURL: Configuration.api.baseURL,
});

// Add a request interceptor
instance.interceptors.request.use(function (config) {
    // Do something before request is sent

    return config;
}, function (error) {
    // Do something with request error
    return Promise.reject(error);
});

/**
 * The expiry message's second sentence, or nothing.
 *
 * Only true for a tablet that has already registered. On one that has not, the
 * next login is exactly what mints the identifier, so promising otherwise would
 * be backwards. The window is narrow - it needs a login that succeeded and a
 * registration that did not - but the Login screen gates the same claim on the
 * same condition, and the two must not disagree about the same tablet.
 */
export function reassuranceForExpiry() {
    return readStoredIdentifier()
        ? ' Signing in again will not set up a new terminal.'
        : '';
}

// Add a response interceptor
instance.interceptors.response.use(function (response) {
    // Do something with response data
    return response;
}, async function (error) {
    // No response means the request never reached Core: offline, DNS, CORS,
    // connection reset. Everything below dereferences error.response, so this
    // has to short-circuit rather than fall through.
    if (!error || !error.response) {
        ToastService.warning('Connection lost. Check the network and try again.', '');
        return Promise.reject(error);
    }

    const originalRequest = error.config;
    // A failing /auth/token must never trigger a refresh - it *is* the refresh.
    const isTokenRequest = originalRequest.url === '/auth/token';
    if (error.response.status === 401 && !originalRequest._retry && !isTokenRequest && loadRefreshToken()) {
        originalRequest._retry = true;
        const outcome = await refreshAccessToken();
        if (outcome !== RefreshOutcome.RENEWED) {
            // Retrying would only send `Bearer null` and take a second 401. When the
            // session ended the tokens are cleared and the logout is already
            // dispatched; when the refresh simply could not be made the session is
            // still intact and must not be reported as expired.
            // The second half of the expiry message is the part that matters. A
            // terminal that reaches here is already registered, so the login it
            // is about to be sent to is a re-authentication - but Core's consent
            // screen lists "Register signature terminal", which staff have read
            // as a prompt to pair the tablet again. Saying so here costs nothing
            // and is repeated on the Login screen itself, because a toast fired
            // at 3am on an unattended kiosk is seen by nobody.
            ToastService.warning(
                outcome === RefreshOutcome.SESSION_ENDED
                    ? 'Your session has expired.' + reassuranceForExpiry()
                    : 'Connection lost. Check the network and try again.',
                ''
            );
            return Promise.reject(error);
        }
        const newToken = loadToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
    }

    const configUrl = error.response.config.url;
    // A caller that words its own failure passes `quiet` and gets no generic
    // toast on top. A 401 still ends the session either way.
    const quiet = !!originalRequest.quiet;

    // Do something with response error
    console.log(error.response);
    switch (error.response.status) {
        case 400:
            if (configUrl === '/auth/token') {
                ToastService.warning('Invalid credentials', '');
            }
            break;
        case 401:
            if (configUrl === '/auth/token') {
                ToastService.warning('Invalid credentials', '');
            } else {
                // Same wording as the refresh path above: this also ends the
                // session and sends the tablet to the Login screen, so it must
                // not read as a different problem.
                ToastService.warning('Your session has expired.' + reassuranceForExpiry(), '');
            }
            store.dispatch(logoutSuccess());

            break;
        case 500:
            // TOAST 500
            // console.error(500);
            break;
        case 405:
            // console.log(error.response.status, error.response.data);
            if (!quiet) {
                ToastService.warning(error.response.data.error, '');
            }
            break;
        case 406:
            // console.log(error.response.status, error.response.data);
            if (!quiet) {
                ToastService.info(error.response.data.error, '');
            }
            break;
        default:
            if (!quiet) {
                ToastService.error('Something went wrong', '');
            }
            break;
    }

    console.error(error.response.status);
    return Promise.reject(error);
});

export default instance;
